import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { history, findAccess } from '../../../utils';
import { adminColumns } from '../../../tableColumns/settingColumns';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import AddIcon from '@mui/icons-material/Add';
import { IAdmin } from '../../../ts/interface/admins.interface';
import Card from '@mui/material/Card';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import { ISortConfig } from '../../../ts/interface/components/redesing/table.interface';
import { useSystemStore } from '../../../store/systemStore';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  useAdminUsersRelayQuery,
  fetchAllAdminUsers,
  AdminUserGraphQL,
  AdminUserSortInput,
} from '../../../hooks/admins/query/V2';
import { removeAdminFromSystemMutation } from '../../../hooks/admins/mutation/V2';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import CustomToast from '../../../components/Redesing/custom-toast';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';

const sortFieldMap: Record<string, AdminUserSortInput['field']> = {
  admin: 'FIRST_NAME',
  firstName: 'FIRST_NAME',
  lastName: 'LAST_NAME',
  email: 'EMAIL',
};

interface AdminProps {
  admin: IAdmin;
  systemAccess: any;
  systemLoaded: boolean;
  systemName: string;
}

const Admin: React.FC<AdminProps> = (props) => {
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortData, setSortData] = useState<ISortConfig>({
    key: 'admin',
    direction: 'asc',
  });
  const [search, setSearch] = React.useState('');
  const [exportData, setExportData] = useState<boolean>(false);

  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedAdmins, setAccumulatedAdmins] = useState<AdminUserGraphQL[]>([]);

  const debouncedSearchTerm = useSearchDebounce(search);

  const { systemInfo } = useSystemStore();
  const systemGid = systemInfo.gid;

  const adminsFilter = useMemo(() => {
    if (!systemGid) return undefined;
    return {
      systemId: systemGid,
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
    };
  }, [systemGid, debouncedSearchTerm]);

  const graphqlSort: AdminUserSortInput[] = useMemo(() => {
    const field = sortFieldMap[sortData.key];
    if (!field) return [{ field: 'FIRST_NAME', order: 'ASC' }];
    return [{ field, order: sortData.direction === 'asc' ? 'ASC' : 'DESC' }];
  }, [sortData]);

  const {
    data: graphqlData,
    count: totalAdmins,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading,
    isRefetching,
    refetch,
  } = useAdminUsersRelayQuery(adminsFilter, {
    limit: 100,
    after: afterCursor,
    sort: graphqlSort,
    enabled: props.systemLoaded && Boolean(systemGid),
  });

  useMemo(() => {
    const admins = graphqlData ?? [];
    if (!admins.length && !afterCursor) {
      setAccumulatedAdmins([]);
      return;
    }
    if (admins.length) {
      if (afterCursor) {
        setAccumulatedAdmins((prev) => [...prev, ...admins]);
      } else {
        setAccumulatedAdmins(admins);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphqlData]);

  const admins = accumulatedAdmins.length > 0 ? accumulatedAdmins : graphqlData ?? [];

  const resetPagination = () => {
    setAccumulatedAdmins([]);
    setAfterCursor(null);
    setPageNo(0);
  };

  const canManageAdmins =
    findAccess(props.systemAccess).includes('admin') ||
    Boolean(props.admin.admin?.movaticAccess);

  const selectedAdmin = useRef<AdminUserGraphQL | null>(null);
  const [removeAdminConfirm, setRemoveAdminConfirm] = useState(false);

  // Remove the admin from the current system via GraphQL, revoking every role
  // and permission they hold there — same flow as the admin details page and
  // the organization admins list.
  const removeAdmin = () => {
    const adminEmail = selectedAdmin.current?.email;
    setRemoveAdminConfirm(false);
    if (!adminEmail || !systemGid) {
      CustomToast({
        message: 'Failed to remove admin',
        type: 'error',
      });
      return;
    }
    CustomAsyncToast({
      promise: () =>
        removeAdminFromSystemMutation({
          email: adminEmail,
          systemId: systemGid,
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors?.[0]?.message || 'Failed to remove admin');
          }
          return res;
        }),
      successMessage: () => 'Admin removed successfully',
      loadingMessage: 'Removing admin...',
      errorMessage: 'There was an error removing the admin',
    }).then((response) => {
      if (response) {
        selectedAdmin.current = null;
        resetPagination();
        refetch();
      }
    });
  };

  // PaginationTable exports only the rows it is given, so load every matching
  // page before triggering the CSV export.
  const handleExport = () => {
    CustomAsyncToast({
      promise: () =>
        fetchAllAdminUsers(adminsFilter, graphqlSort).then((allAdmins) => {
          setAccumulatedAdmins(allAdmins);
          setExportData(true);
        }),
      successMessage: () => 'Admins exported successfully',
      loadingMessage: 'Preparing export...',
      errorMessage: 'There was an error exporting the admins',
    });
  };

  return (
    <Card>
      <PaginationTable
        padding={{
          //pl: -1,
          pt: 1,
          pr: 1,
        }}
        showFilter={false}
        sortBy={sortData.key}
        sortDirection={sortData.direction}
        isLoading={
          isLoading ||
          !props.systemLoaded ||
          (pageNo > 0 && admins.length <= pageNo * pageSize)
        }
        isLoadingCache={isRefetching}
        onSortChange={(sort) => {
          resetPagination();
          setSortData(sort as ISortConfig);
        }}
        items={admins}
        totalItems={totalAdmins}
        hasMore={graphqlHasMore || admins.length > (pageNo + 1) * pageSize}
        localSearch={false}
        customKey={'email'}
        noDataText={`No admins found`}
        onRowCLick={(email: string) => history.push(`/settings/admin/${email}`)}
        columns={adminColumns(
          (row: AdminUserGraphQL) => history.push(`/settings/admin/${row.email}?edit=1`),
          canManageAdmins
            ? (row: AdminUserGraphQL) => {
                selectedAdmin.current = row;
                setRemoveAdminConfirm(true);
              }
            : undefined
        )}
        rowsPerPage={pageSize}
        page={pageNo}
        onPageChange={(page, pageN: number) => {
          const incrementedPage = pageN + 1;
          if (admins?.length && admins.length / pageSize < incrementedPage) {
            setAfterCursor(endCursor || null);
          }
          setPageNo(pageN);
        }}
        onRowsPerPageChange={(rowsPerPage) => {
          resetPagination();
          setPageSize(Number(rowsPerPage.target.value));
        }}
        searchText={search}
        searchPlaceholder={`Search admins`}
        handleSearch={(searchEvent) => {
          resetPagination();
          setSearch(searchEvent.target.value);
        }}
        exportData={exportData}
        csvFileName={'System_Admins.csv'}
        onExport={() => setExportData(false)}
        buttons={[
          {
            label: 'Export',
            startIcon: <FileDownloadIcon />,
            onClick: () => handleExport(),
          },
          {
            label: 'Add',
            startIcon: <AddIcon />,
            onClick: () => history.push('/settings/admin/add'),
            disabled: !canManageAdmins,
          },
        ]}
      />
      <MovaticCustomModal
        title={'Remove Admin'}
        open={removeAdminConfirm}
        onClose={() => setRemoveAdminConfirm(false)}
        content={
          <div>
            Are you sure you would like to remove{' '}
            <b>
              {`${selectedAdmin.current?.firstName || ''} ${
                selectedAdmin.current?.lastName || ''
              }`.trim() || selectedAdmin.current?.email}
            </b>{' '}
            from the system <b>{props.systemName}</b>?
          </div>
        }
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setRemoveAdminConfirm(false),
              },
              {
                label: 'Remove',
                onClick: () => removeAdmin(),
              },
            ]}
          />
        }
      />
    </Card>
  );
};

export default connect(
  (state: {
    admin: {
      admin: IAdmin;
      systemAccess: string;
    };
    system: {
      isLoaded: boolean;
      current: { name?: string };
    };
  }) => ({
    admin: state.admin.admin,
    systemAccess: state.admin.systemAccess,
    systemLoaded: state.system.isLoaded,
    systemName: state.system.current?.name || '',
  }),
  () => ({})
)(Admin);
